import { useState, useEffect } from "react";
import { useUser, useClerk } from "@clerk/clerk-react";
import { supabase } from "./supabaseClient";

function TallyModal({ onClose }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      backdropFilter: "blur(8px)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 1000, padding: 24
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#0f0f18", border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 20, width: "100%", maxWidth: 540,
        maxHeight: "90vh", overflow: "hidden",
        boxShadow: "0 40px 80px rgba(0,0,0,0.6)", display: "flex", flexDirection: "column"
      }}>
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)"
        }}>
          <div>
            <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, fontStyle: "italic", color: "#e8e4dc" }}>
              How can we improve Zeno?
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(232,228,220,0.35)", marginTop: 4 }}>
              Your voice shapes what we build next
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "rgba(232,228,220,0.4)", fontSize: 20, lineHeight: 1, padding: 4
          }}>✕</button>
        </div>

        {/* Tally Embed */}
        <iframe
          src="https://tally.so/embed/jaMGN6?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1"
          loading="lazy"
          width="100%"
          height="500"
          frameBorder="0"
          marginHeight="0"
          marginWidth="0"
          title="How can we improve Zeno Finance?"
          style={{ background: "transparent", padding: "8px 0", border: "none" }}
        />
      </div>
    </div>
  );
}

const fmt = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0 });
const pct = (a, b) => Math.min(100, Math.round((a / b) * 100));

function Ring({ value, max, color, size = 100, stroke = 8 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const [progress, setProgress] = useState(0);
  useEffect(() => { setTimeout(() => setProgress(value / max), 100); }, [value, max]);
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={circ * (1 - progress)} strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)" }} />
    </svg>
  );
}

function Bar({ value, max, color }) {
  const [w, setW] = useState(0);
  useEffect(() => { setTimeout(() => setW(pct(value, max)), 150); }, [value, max]);
  return (
    <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 99, height: 6, overflow: "hidden" }}>
      <div style={{ width: w + "%", background: color, height: "100%", borderRadius: 99, transition: "width 1s cubic-bezier(.4,0,.2,1)" }} />
    </div>
  );
}

function ProgressBar({ value, max, color = "#6366f1" }) {
  const [w, setW] = useState(0);
  const percent = pct(value, max);
  useEffect(() => { setTimeout(() => setW(Math.min(Math.max(percent, 0), 100)), 150); }, [value, max]);
  return (
    <div style={{
      width: w + "%", height: "100%", borderRadius: 99,
      background: `linear-gradient(90deg, #6366f1, ${color})`,
      transition: "width 1.2s cubic-bezier(.4,0,.2,1)",
      boxShadow: `0 0 12px ${color}66`
    }} />
  );
}

const statusColor = { active: "#4ade80", slow: "#facc15", "at-risk": "#f87171" };
const statusLabel = { active: "Profitable", slow: "Monitor", "at-risk": "At Risk" };

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@300;400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #0a0a0f; }
  ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: #0a0a0f; } ::-webkit-scrollbar-thumb { background: #333; border-radius: 2px; }
  .tab-btn { background: none; border: none; cursor: pointer; font-family: 'DM Mono', monospace; font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; padding: 8px 16px; border-radius: 99px; transition: all 0.2s; white-space: nowrap; }
  .tab-btn:hover { background: rgba(255,255,255,0.05); }
  .tab-btn.active { background: rgba(255,255,255,0.1); color: #fff; }
  .card { background: rgba(255,255,255,0.035); border: 1px solid rgba(255,255,255,0.07); border-radius: 16px; padding: 20px; }
  .card:hover { border-color: rgba(255,255,255,0.12); transition: border-color 0.3s; }
  .mono { font-family: 'DM Mono', monospace; }
  .label { font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: rgba(232,228,220,0.4); }
  .big-num { font-size: 36px; font-weight: 400; letter-spacing: -0.02em; line-height: 1; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
  .insight { background: linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.06)); border: 1px solid rgba(99,102,241,0.2); border-radius: 12px; padding: 16px; font-family: 'DM Mono', monospace; font-size: 12px; line-height: 1.6; color: rgba(232,228,220,0.75); }
  .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
  .input-field { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 10px 14px; color: #e8e4dc; font-family: 'DM Mono', monospace; font-size: 13px; outline: none; width: 100%; }
  .btn-primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none; border-radius: 8px; padding: 10px 20px; color: #fff; font-family: 'DM Mono', monospace; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; }
  .btn-danger { background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.2); border-radius: 8px; padding: 6px 12px; color: #f87171; font-family: 'DM Mono', monospace; font-size: 10px; cursor: pointer; }
  @media (max-width: 640px) {
    .grid2 { grid-template-columns: 1fr 1fr; gap: 10px; }
    .grid3 { grid-template-columns: 1fr 1fr; gap: 10px; }
    .big-num { font-size: 22px; }
    .card { padding: 16px; }
    .tab-btn { font-size: 9px; padding: 6px 10px; letter-spacing: 0.08em; }
  }
`;

export default function Dashboard() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [activeTab, setActiveTab] = useState("overview");
  const [showFeedback, setShowFeedback] = useState(false);
  const [clients, setClients] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settings, setSettings] = useState({ tax_rate: 0.25, monthly_goal: 10000, runway_savings: 5000 });
  const [income, setIncome] = useState(0);
  const [loading, setLoading] = useState(true);

  // Add forms
  const [newClient, setNewClient] = useState({ name: "", revenue: "", hours: "", status: "active" });
  const [newExpense, setNewExpense] = useState({ category: "", amount: "", tax_deductible: false });

  const userId = user?.id;

  useEffect(() => {
    if (userId) loadData();
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    const month = new Date().toISOString().slice(0, 7);
    const [c, e, s, inc] = await Promise.all([
      supabase.from("clients").select("*").eq("user_id", userId),
      supabase.from("expenses").select("*").eq("user_id", userId),
      supabase.from("user_settings").select("*").eq("user_id", userId).single(),
      supabase.from("income").select("*").eq("user_id", userId).eq("month", month).single()
    ]);
    if (c.data) setClients(c.data);
    if (e.data) setExpenses(e.data);
    if (s.data) setSettings(s.data);
    if (inc.data) setIncome(inc.data.amount || 0);
    setLoading(false);
  };

  const addClient = async () => {
    if (!newClient.name) return;
    const { data } = await supabase.from("clients").insert([{ ...newClient, user_id: userId, revenue: +newClient.revenue, hours: +newClient.hours }]).select();
    if (data) { setClients([...clients, data[0]]); setNewClient({ name: "", revenue: "", hours: "", status: "active" }); }
  };

  const addExpense = async () => {
    if (!newExpense.category || !newExpense.amount) return;
    const { data } = await supabase.from("expenses").insert([{ ...newExpense, user_id: userId, amount: +newExpense.amount }]).select();
    if (data) { setExpenses([...expenses, data[0]]); setNewExpense({ category: "", amount: "", tax_deductible: false }); }
  };

  const deleteClient = async (id) => {
    await supabase.from("clients").delete().eq("id", id);
    setClients(clients.filter(c => c.id !== id));
  };

  const deleteExpense = async (id) => {
    await supabase.from("expenses").delete().eq("id", id);
    setExpenses(expenses.filter(e => e.id !== id));
  };

  const saveIncome = async (val) => {
    const month = new Date().toISOString().slice(0, 7);
    setIncome(+val);
    await supabase.from("income").upsert([{ user_id: userId, amount: +val, month, goal: settings.monthly_goal }], { onConflict: "user_id,month" });
  };

  const totalExpenses = expenses.reduce((s, e) => s + +e.amount, 0);
  const taxDeductible = expenses.filter(e => e.tax_deductible).reduce((s, e) => s + +e.amount, 0);
  
  // Auto-calculate income from all clients combined
  const clientRevenue = clients.reduce((s, c) => s + +c.revenue, 0);
  const effectiveIncome = clientRevenue > 0 ? clientRevenue : income;
  
  const estimatedTax = Math.round((effectiveIncome - taxDeductible) * (settings.tax_rate || 0.25));
  const netIncome = effectiveIncome - totalExpenses - estimatedTax;
  const monthlyBurn = totalExpenses + estimatedTax;
  const runway = monthlyBurn > 0 ? (settings.runway_savings / monthlyBurn).toFixed(1) : "∞";
  const topClient = clients.length > 0 ? [...clients].sort((a, b) => (b.revenue / b.hours) - (a.revenue / a.hours))[0] : null;

  const tabs = ["overview", "clients", "taxes", "runway", "settings"];

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#0a0a0f", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Mono', monospace", color: "rgba(232,228,220,0.4)", fontSize: 12, letterSpacing: "0.15em" }}>
      LOADING ZENO...
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0f", fontFamily: "'DM Serif Display', Georgia, serif", color: "#e8e4dc", backgroundImage: "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99,102,241,0.12) 0%, transparent 70%)" }}>
      <style>{STYLES}</style>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px 0" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="label" style={{ marginBottom: 4 }}>Zeno Finance</div>
            <h1 style={{ fontSize: 22, fontWeight: 400, letterSpacing: "-0.02em", fontStyle: "italic" }}>Your Dashboard</h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div className="mono" style={{ fontSize: 10, color: "rgba(232,228,220,0.3)", display: "none" }}>{user?.emailAddresses?.[0]?.emailAddress}</div>
            <button onClick={() => setShowFeedback(true)}
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "6px 12px", color: "rgba(232,228,220,0.6)", fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer", letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
              Feedback
            </button>
            <button onClick={() => window.open("https://billing.stripe.com/p/login/9B6eVfdbsd88das3O5fAc00", "_blank")}
              style={{ background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: 8, padding: "6px 12px", color: "#a5b4fc", fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer", letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
              Plan
            </button>
            <button className="btn-danger" onClick={() => signOut()}>Sign out</button>
          </div>
        </div>

        {showFeedback && <TallyModal onClose={() => setShowFeedback(false)} />}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "rgba(255,255,255,0.03)", borderRadius: 99, padding: 4, overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}>
          {tabs.map(t => (
            <button key={t} className={`tab-btn ${activeTab === t ? "active" : ""}`}
              style={{ color: activeTab === t ? "#fff" : "rgba(232,228,220,0.45)", flexShrink: 0 }}
              onClick={() => setActiveTab(t)}>{t}</button>
          ))}
        </div>

        {/* OVERVIEW */}
        {activeTab === "overview" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 40 }}>

            {/* Big Progress Card */}
            <div className="card" style={{ padding: "28px 28px 24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div>
                  <div className="label" style={{ marginBottom: 6 }}>Monthly Income Goal</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                    <div style={{ fontSize: 36, fontStyle: "italic", letterSpacing: "-0.02em" }}>{fmt(effectiveIncome)}</div>
                    <span className="mono" style={{ fontSize: 13, color: "rgba(232,228,220,0.3)" }}>of {fmt(settings.monthly_goal || 10000)}</span>
                  </div>
                  {clients.length > 0
                    ? <div className="mono" style={{ fontSize: 10, color: "rgba(232,228,220,0.3)", marginTop: 4 }}>
                        ✦ Auto-calculated from {clients.length} client{clients.length > 1 ? "s" : ""}
                      </div>
                    : <div className="mono" style={{ fontSize: 10, color: "rgba(232,228,220,0.3)", marginTop: 4 }}>
                        Add clients to track income automatically
                      </div>
                  }
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="label" style={{ marginBottom: 6 }}>Net Take-Home</div>
                  <div style={{ fontSize: 26, color: netIncome >= 0 ? "#4ade80" : "#f87171" }}>{fmt(netIncome)}</div>
                  <div className="mono" style={{ fontSize: 10, color: "rgba(232,228,220,0.3)", marginTop: 4 }}>
                    after tax & expenses
                  </div>
                </div>
              </div>

              {/* 3 stacked bars — gross, after expenses, after tax */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span className="mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: "rgba(232,228,220,0.4)" }}>GROSS REVENUE</span>
                    <span className="mono" style={{ fontSize: 9, color: "rgba(232,228,220,0.4)" }}>{fmt(effectiveIncome)}</span>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 99, height: 18, overflow: "hidden", position: "relative" }}>
                    <ProgressBar value={effectiveIncome} max={settings.monthly_goal || 10000} color="#6366f1" />
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span className="mono" style={{ fontSize: 10, color: "#fff", letterSpacing: "0.1em", mixBlendMode: "difference" }}>
                        {pct(effectiveIncome, settings.monthly_goal || 10000)}%
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span className="mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: "rgba(232,228,220,0.4)" }}>AFTER EXPENSES</span>
                    <span className="mono" style={{ fontSize: 9, color: "rgba(232,228,220,0.4)" }}>− {fmt(totalExpenses)}</span>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 99, height: 10, overflow: "hidden" }}>
                    <ProgressBar value={effectiveIncome - totalExpenses} max={settings.monthly_goal || 10000} color="#a78bfa" />
                  </div>
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span className="mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: "rgba(232,228,220,0.4)" }}>AFTER TAX</span>
                    <span className="mono" style={{ fontSize: 9, color: "rgba(232,228,220,0.4)" }}>− {fmt(estimatedTax)}</span>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 99, height: 10, overflow: "hidden" }}>
                    <ProgressBar value={netIncome} max={settings.monthly_goal || 10000} color="#4ade80" />
                  </div>
                </div>
              </div>

              {/* Milestone markers */}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                {[0, 25, 50, 75, 100].map(m => (
                  <div key={m} style={{ textAlign: "center" }}>
                    <div className="mono" style={{
                      fontSize: 9, letterSpacing: "0.1em",
                      color: pct(effectiveIncome, settings.monthly_goal || 10000) >= m ? "#6366f1" : "rgba(232,228,220,0.2)"
                    }}>{m}%</div>
                  </div>
                ))}
              </div>

              {/* Status message */}
              <div className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.4)", textAlign: "center" }}>
                {pct(effectiveIncome, settings.monthly_goal || 10000) >= 100
                  ? "🎉 Goal reached! You crushed it this month."
                  : pct(effectiveIncome, settings.monthly_goal || 10000) >= 75
                  ? `🔥 Almost there! ${fmt(Math.max(0, (settings.monthly_goal || 10000) - effectiveIncome))} to go`
                  : pct(effectiveIncome, settings.monthly_goal || 10000) >= 50
                  ? `💪 Halfway there! ${fmt(Math.max(0, (settings.monthly_goal || 10000) - effectiveIncome))} remaining`
                  : `🎯 Keep going! ${fmt(Math.max(0, (settings.monthly_goal || 10000) - effectiveIncome))} remaining this month`
                }
              </div>
            </div>

            <div className="grid3">
              <div className="card">
                <div className="label" style={{ marginBottom: 12 }}>Total Expenses</div>
                <div style={{ fontSize: 28 }}>{fmt(totalExpenses)}</div>
                <div className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.4)", marginTop: 6 }}>{fmt(taxDeductible)} deductible</div>
              </div>
              <div className="card">
                <div className="label" style={{ marginBottom: 12 }}>Est. Tax Owed</div>
                <div style={{ fontSize: 28, color: "#facc15" }}>{fmt(estimatedTax)}</div>
                <div className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.4)", marginTop: 6 }}>at {Math.round(settings.tax_rate * 100)}% rate</div>
              </div>
              <div className="card">
                <div className="label" style={{ marginBottom: 12 }}>Cash Runway</div>
                <div style={{ fontSize: 28, color: +runway > 3 ? "#4ade80" : "#f87171" }}>{runway} mo</div>
                <div className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.4)", marginTop: 6 }}>at current burn</div>
              </div>
            </div>

            {topClient && (
              <div className="insight">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span>✦</span>
                  <span style={{ color: "#a5b4fc", letterSpacing: "0.1em", fontSize: 10, textTransform: "uppercase" }}>AI Insight</span>
                </div>
                Your top client <strong style={{ color: "#e8e4dc" }}>{topClient.name}</strong> generates {fmt(Math.round(topClient.revenue / topClient.hours))}/hr.
                {clients.length > 1 && ` Focus on acquiring more clients like them to hit your ${fmt(settings.monthly_goal)} goal faster.`}
              </div>
            )}
            {!topClient && (
              <div className="insight">
                <span style={{ color: "#a5b4fc", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>✦ Getting Started  </span>
                Add your clients and expenses to unlock AI insights about your freelance business.
              </div>
            )}
          </div>
        )}

        {/* CLIENTS */}
        {activeTab === "clients" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 40 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div className="label">Client Profitability</div>
              <div style={{ display: "flex", gap: 12 }}>
                {[["#4ade80", "Active — top 80%+"], ["#facc15", "Slow — 40–79%"], ["#f87171", "At Risk — under 40%"]].map(([color, label]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span className="dot" style={{ background: color }} />
                    <span className="mono" style={{ fontSize: 9, color: "rgba(232,228,220,0.4)" }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Add client — removed manual status */}
            <div className="card">
              <div className="label" style={{ marginBottom: 14 }}>Add Client</div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
                <div><div className="label" style={{ marginBottom: 6 }}>Name</div><input className="input-field" value={newClient.name} onChange={e => setNewClient({...newClient, name: e.target.value})} placeholder="Client name" /></div>
                <div><div className="label" style={{ marginBottom: 6 }}>Revenue $</div><input className="input-field" type="number" value={newClient.revenue} onChange={e => setNewClient({...newClient, revenue: e.target.value})} placeholder="0" /></div>
                <div><div className="label" style={{ marginBottom: 6 }}>Hours</div><input className="input-field" type="number" value={newClient.hours} onChange={e => setNewClient({...newClient, hours: e.target.value})} placeholder="0" /></div>
                <button className="btn-primary" onClick={addClient} style={{ marginTop: 20 }}>Add</button>
              </div>
              <div className="mono" style={{ fontSize: 10, color: "rgba(232,228,220,0.3)", marginTop: 10 }}>✦ Status is calculated automatically based on hourly rate</div>
            </div>

            {clients.length === 0 && <div className="card" style={{ textAlign: "center", padding: 40, color: "rgba(232,228,220,0.3)", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>No clients yet — add your first one above</div>}

            {[...clients].sort((a, b) => (b.revenue / b.hours) - (a.revenue / a.hours)).map((c, i) => {
              const rate = c.hours > 0 ? Math.round(c.revenue / c.hours) : 0;
              const topRate = Math.max(...clients.map(cl => cl.hours > 0 ? cl.revenue / cl.hours : 0));
              const ratio = topRate > 0 ? rate / topRate : 0;
              const autoStatus = ratio >= 0.8 ? "active" : ratio >= 0.4 ? "slow" : "at-risk";
              const maxRate = Math.max(200, topRate);
              return (
                <div key={c.id} className="card" style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div className="mono" style={{ fontSize: 22, color: "rgba(232,228,220,0.15)", width: 28, flexShrink: 0 }}>0{i+1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                      <span style={{ fontSize: 18 }}>{c.name}</span>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span className="dot" style={{ background: statusColor[autoStatus] }} />
                        <span className="mono" style={{ fontSize: 10, color: statusColor[autoStatus], letterSpacing: "0.1em" }}>{statusLabel[autoStatus]}</span>
                      </span>
                    </div>
                    <Bar value={rate} max={maxRate} color={statusColor[autoStatus]} />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                      <span className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.4)" }}>{fmt(c.revenue)} · {c.hours}h · {Math.round(ratio * 100)}% of top rate</span>
                      <span className="mono" style={{ fontSize: 13 }}>{fmt(rate)}/hr</span>
                    </div>
                  </div>
                  <button className="btn-danger" onClick={() => deleteClient(c.id)}>✕</button>
                </div>
              );
            })}

            {clients.length > 1 && (
              <div className="insight">
                <span style={{ color: "#a5b4fc", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>✦ AI Insight  </span>
                {(() => {
                  const topRate = Math.max(...clients.map(c => c.hours > 0 ? c.revenue / c.hours : 0));
                  const atRisk = clients.filter(c => c.hours > 0 && (c.revenue / c.hours) / topRate < 0.4);
                  const slow = clients.filter(c => c.hours > 0 && (c.revenue / c.hours) / topRate >= 0.4 && (c.revenue / c.hours) / topRate < 0.8);
                  if (atRisk.length > 0) return <span>🔴 <strong style={{ color: "#e8e4dc" }}>{atRisk.map(c => c.name).join(", ")}</strong> {atRisk.length === 1 ? "is" : "are"} at-risk — earning less than 40% of your top rate. Consider renegotiating or replacing {atRisk.length === 1 ? "them" : "these clients"}.</span>;
                  if (slow.length > 0) return <span>⚠️ <strong style={{ color: "#e8e4dc" }}>{slow.map(c => c.name).join(", ")}</strong> {slow.length === 1 ? "is" : "are"} below your average rate. A rate increase conversation could significantly boost your income.</span>;
                  return <span>✅ All your clients are performing well! Focus on finding more clients similar to your top earner.</span>;
                })()}
              </div>
            )}
          </div>
        )}

        {/* TAXES */}
        {activeTab === "taxes" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 40 }}>
            <div className="grid2">
              <div className="card"><div className="label" style={{ marginBottom: 12 }}>Gross Income</div><div className="big-num">{fmt(effectiveIncome)}</div></div>
              <div className="card"><div className="label" style={{ marginBottom: 12 }}>Tax Deductions</div><div className="big-num" style={{ color: "#4ade80" }}>− {fmt(taxDeductible)}</div></div>
            </div>

            {/* Add expense */}
            <div className="card">
              <div className="label" style={{ marginBottom: 14 }}>Add Expense</div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto auto", gap: 10, alignItems: "end" }}>
                <div><div className="label" style={{ marginBottom: 6 }}>Category</div><input className="input-field" value={newExpense.category} onChange={e => setNewExpense({...newExpense, category: e.target.value})} placeholder="e.g. Software" /></div>
                <div><div className="label" style={{ marginBottom: 6 }}>Amount $</div><input className="input-field" type="number" value={newExpense.amount} onChange={e => setNewExpense({...newExpense, amount: e.target.value})} placeholder="0" /></div>
                <div style={{ paddingBottom: 2 }}>
                  <div className="label" style={{ marginBottom: 6 }}>Deductible</div>
                  <input type="checkbox" checked={newExpense.tax_deductible} onChange={e => setNewExpense({...newExpense, tax_deductible: e.target.checked})} style={{ width: 18, height: 18, marginTop: 8 }} />
                </div>
                <button className="btn-primary" onClick={addExpense} style={{ marginBottom: 2 }}>Add</button>
              </div>
            </div>

            <div className="card">
              <div className="label" style={{ marginBottom: 16 }}>Expenses</div>
              {expenses.length === 0 && <div className="mono" style={{ fontSize: 12, color: "rgba(232,228,220,0.3)", textAlign: "center", padding: "20px 0" }}>No expenses yet</div>}
              {expenses.map(e => (
                <div key={e.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="dot" style={{ background: e.tax_deductible ? "#4ade80" : "rgba(232,228,220,0.2)" }} />
                    <span style={{ fontSize: 15 }}>{e.category}</span>
                    {e.tax_deductible && <span className="mono" style={{ fontSize: 9, background: "rgba(74,222,128,0.1)", color: "#4ade80", padding: "2px 6px", borderRadius: 4, letterSpacing: "0.1em" }}>DEDUCTIBLE</span>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span className="mono" style={{ fontSize: 14 }}>{fmt(e.amount)}</span>
                    <button className="btn-danger" onClick={() => deleteExpense(e.id)}>✕</button>
                  </div>
                </div>
              ))}
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 14, marginTop: 4, display: "flex", justifyContent: "space-between" }}>
                <span className="label">Estimated Tax Owed</span>
                <span className="mono" style={{ fontSize: 16, color: "#facc15" }}>{fmt(estimatedTax)}</span>
              </div>
            </div>

            <div className="insight">
              <span style={{ color: "#a5b4fc", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>✦ AI Insight  </span>
              Set aside {fmt(estimatedTax)} this month for taxes. Your {fmt(taxDeductible)} in deductions saved you ~{fmt(Math.round(taxDeductible * settings.tax_rate))} in taxes.
            </div>
          </div>
        )}

        {/* RUNWAY */}
        {activeTab === "runway" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 40 }}>
            <div className="card" style={{ textAlign: "center", padding: "48px 24px" }}>
              <div className="label" style={{ marginBottom: 16 }}>Cash Runway</div>
              <div style={{ fontSize: 80, fontStyle: "italic", color: +runway > 3 ? "#4ade80" : "#f87171", lineHeight: 1 }}>{runway}</div>
              <div style={{ fontSize: 20, color: "rgba(232,228,220,0.5)", marginTop: 8 }}>months</div>
              <div className="mono" style={{ fontSize: 12, color: "rgba(232,228,220,0.35)", marginTop: 16 }}>at {fmt(monthlyBurn)}/mo burn rate</div>
            </div>

            <div className="card">
              <div className="label" style={{ marginBottom: 14 }}>Update Savings</div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <input className="input-field" type="number" value={settings.runway_savings || ""} onChange={e => setSettings({...settings, runway_savings: +e.target.value})} placeholder="Total savings / cash reserves" />
                <button className="btn-primary" onClick={async () => {
                  await supabase.from("user_settings").upsert([{ ...settings, user_id: userId }], { onConflict: "user_id" });
                }}>Save</button>
              </div>
            </div>

            <div className="grid2">
              <div className="card">
                <div className="label" style={{ marginBottom: 10 }}>Monthly Burn</div>
                <div style={{ fontSize: 26 }}>{fmt(monthlyBurn)}</div>
                <div className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.4)", marginTop: 6 }}>expenses + taxes</div>
              </div>
              <div className="card">
                <div className="label" style={{ marginBottom: 10 }}>6-Month Safety Fund</div>
                <div style={{ fontSize: 26, color: "#6366f1" }}>{fmt(monthlyBurn * 6)}</div>
                <div className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.4)", marginTop: 6 }}>recommended target</div>
              </div>
            </div>

            <div className="insight">
              <span style={{ color: "#a5b4fc", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>✦ AI Insight  </span>
              {+runway >= 6
                ? `Great work! Your ${runway}-month runway exceeds the recommended 6 months. You're in a strong, stable position.`
                : `Your runway is below the recommended 6 months. At your current net income of ${fmt(netIncome)}/mo, save at least ${fmt(Math.round(monthlyBurn * 0.5))} extra per month to build your safety net.`
              }
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {activeTab === "settings" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 40 }}>
            <div className="label" style={{ marginBottom: 4 }}>Your Preferences</div>

            <div className="card">
              <div className="label" style={{ marginBottom: 20 }}>Financial Goals</div>

              <div style={{ marginBottom: 20 }}>
                <div className="label" style={{ marginBottom: 8 }}>Monthly Income Goal</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="mono" style={{ color: "rgba(232,228,220,0.4)", fontSize: 16 }}>$</span>
                  <input className="input-field" type="number"
                    value={settings.monthly_goal || ""}
                    onChange={e => setSettings({...settings, monthly_goal: +e.target.value})}
                    placeholder="e.g. 10000" style={{ fontSize: 20 }} />
                </div>
                <div className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.3)", marginTop: 6 }}>
                  This sets the target on your income progress ring
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div className="label" style={{ marginBottom: 8 }}>Tax Rate (%)</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input className="input-field" type="number"
                    value={Math.round((settings.tax_rate || 0.25) * 100)}
                    onChange={e => setSettings({...settings, tax_rate: +e.target.value / 100})}
                    placeholder="25" style={{ fontSize: 20 }} />
                  <span className="mono" style={{ color: "rgba(232,228,220,0.4)", fontSize: 16 }}>%</span>
                </div>
                <div className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.3)", marginTop: 6 }}>
                  Used to calculate your estimated tax owed
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <div className="label" style={{ marginBottom: 8 }}>Current Savings / Cash Reserves</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="mono" style={{ color: "rgba(232,228,220,0.4)", fontSize: 16 }}>$</span>
                  <input className="input-field" type="number"
                    value={settings.runway_savings || ""}
                    onChange={e => setSettings({...settings, runway_savings: +e.target.value})}
                    placeholder="e.g. 5000" style={{ fontSize: 20 }} />
                </div>
                <div className="mono" style={{ fontSize: 11, color: "rgba(232,228,220,0.3)", marginTop: 6 }}>
                  Used to calculate your cash runway
                </div>
              </div>

              <button className="btn-primary" style={{ width: "100%", padding: "14px" }} onClick={async () => {
                await supabase.from("user_settings").upsert([{ ...settings, user_id: userId }], { onConflict: "user_id" });
                alert("✅ Settings saved!");
              }}>Save Settings →</button>
            </div>

            <div className="insight">
              <span style={{ color: "#a5b4fc", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>✦ AI Insight  </span>
              {settings.monthly_goal
                ? `Your ${fmt(settings.monthly_goal)} goal at ${Math.round((settings.tax_rate || 0.25) * 100)}% tax means you need to earn ${fmt(Math.round(settings.monthly_goal / (1 - (settings.tax_rate || 0.25))))} gross to take home your target amount.`
                : `Set your monthly income goal above to unlock personalized progress tracking across your dashboard.`
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
